import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, MapPin, Calendar, FunnelSimple, X, Compass } from "@phosphor-icons/react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { confImage } from "@/lib/images";
import EmptyState from "@/components/EmptyState";
import { SkeletonCardGrid } from "@/components/Skeleton";
import { prefs } from "@/lib/native";

const CACHE_KEY = "symposia.confs.default";

export default function ConferencesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [topics, setTopics] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryCounts, setCountryCounts] = useState({});
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState(searchParams.get("topic") || "");
  const [type, setType] = useState("all");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/meta/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/meta/countries").then((r) => setCountries(r.data)).catch(() => {});
    api.get("/meta/country-counts").then((r) => {
      const map = {};
      (r.data || []).forEach((row) => { map[row.name] = row.count; });
      setCountryCounts(map);
    }).catch(() => {});
  }, []);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [q, topic, type, country]);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 24 };
    if (q) params.q = q;
    if (topic) params.topic = topic;
    if (type) params.type = type;
    if (country) params.country = country;
    // Instant paint from cache while the network request is in flight.
    // Only for the default (unfiltered) view — cached data must not confuse users.
    const cacheable = !q && !topic && (type === "all" || !type) && !country && page === 1;
    if (cacheable) {
      prefs.get(CACHE_KEY).then((raw) => {
        if (!raw || list.length) return;
        try {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached?.items)) {
            setList(cached.items);
            setTotal(cached.total || cached.items.length);
          }
        } catch { /* stale cache — ignore */ }
      });
    }
    const timer = setTimeout(() => {
      api.get("/conferences", { params }).then((r) => {
        const items = r.data.items || [];
        setList(items);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
        if (cacheable) {
          // Only cache the top 24 (default view) — offline users see the first page.
          prefs.set(CACHE_KEY, JSON.stringify({ items, total: r.data.total, cached_at: Date.now() })).catch(() => {});
        }
      }).catch(() => {
        // Network failed — cache paint above may already be visible; nothing extra to do.
      }).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, topic, type, country, page]);

  const hasFilters = q || topic || (type !== "all" && type) || country;

  const typeLabels = { all: t("common.all"), academic: t("common.academic"), tech: t("common.industry") };

  return (
    <div data-testid="conferences-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">{t("conf.breadcrumb")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-none mb-3">{t("conf.allTitle")}</h1>
      <p className="text-zinc-600 max-w-2xl text-base">{t("conf.allIntro")}</p>

      {/* Regional quick-jump pills — one-tap MENA browsing */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="country-pills">
        {[
          { key: "Saudi Arabia", label: "🇸🇦 " + t("conf.countryKSA", { defaultValue: "Saudi Arabia" }) },
          { key: "United Arab Emirates", label: "🇦🇪 " + t("conf.countryUAE", { defaultValue: "UAE" }) },
          { key: "Qatar", label: "🇶🇦 " + t("conf.countryQatar", { defaultValue: "Qatar" }) },
          { key: "Morocco", label: "🇲🇦 " + t("conf.countryMorocco", { defaultValue: "Morocco" }) },
          { key: "Egypt", label: "🇪🇬 " + t("conf.countryEgypt", { defaultValue: "Egypt" }) },
          { key: "Jordan", label: "🇯🇴 " + t("conf.countryJordan", { defaultValue: "Jordan" }) },
        ].map((pill) => {
          const count = countryCounts[pill.key];
          if (count === undefined || count === 0) return null; // hide empties
          const active = country === pill.key;
          return (
            <button
              key={pill.key}
              type="button"
              data-testid={`country-pill-${pill.key.replace(/\s+/g, "-")}`}
              onClick={() => setCountry(active ? "" : pill.key)}
              className={`text-xs font-mono uppercase tracking-[0.14em] px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-blue-700 text-white border-blue-700 dark:bg-blue-500 dark:border-blue-500"
                  : "border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-blue-700 hover:text-blue-700 dark:hover:border-blue-400 dark:hover:text-blue-400"
              }`}
            >
              {pill.label} <span className="opacity-60">· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-12 border-t border-zinc-950 pt-6 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-5">
          <label className="overline block mb-2">{t("common.search")}</label>
          <div className="flex items-center gap-2 border-b border-zinc-300 py-2 focus-within:border-zinc-950 transition-colors">
            <MagnifyingGlass size={16} className="text-zinc-400" />
            <input
              data-testid="conf-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("conf.cardSearchPlaceholder")}
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
        </div>
        <FilterSelect label={t("common.topic")} value={topic} onChange={setTopic} options={topics} testid="conf-filter-topic" allLabel={t("common.all")} />
        <FilterSelect label={t("common.type")} value={type} onChange={setType} options={["all", "academic", "tech"]} testid="conf-filter-type" labels={typeLabels} allLabel={t("common.all")} />
        <FilterSelect label={t("common.country")} value={country} onChange={setCountry} options={countries} testid="conf-filter-country" allLabel={t("common.all")} />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="text-xs font-mono text-zinc-500" data-testid="conf-count">
          {loading ? t("common.loading") : t("common.results", { n: total })}
        </div>
        {hasFilters && (
          <button onClick={() => { setQ(""); setTopic(""); setType("all"); setCountry(""); }} data-testid="conf-clear-filters" className="text-xs font-mono uppercase tracking-wider flex items-center gap-1 text-zinc-500 hover:text-zinc-950">
            <X size={12} /> {t("common.clearFilters")}
          </button>
        )}
      </div>

      {/* Grid of cards */}
      {loading ? (
        <SkeletonCardGrid count={9} testid="conf-skeleton" />
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.length === 0 && (
            <EmptyState
              testid="conf-empty"
              icon={<Compass size={22} />}
              title={t("conf.noMatch")}
              body={t("conf.noMatchBody")}
              action={hasFilters ? { label: t("common.clearFilters"), to: "/conferences" } : null}
            />
          )}
          {list.map((c, idx) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
            <Link to={`/conferences/${c.id}`} data-testid={`conf-row-${idx}`} className="block card overflow-hidden group h-full">
              <div className="aspect-[16/9] bg-zinc-100 overflow-hidden border-b border-zinc-200 relative">
                <img src={confImage(c)} alt={c.title} loading="lazy" className="img-mono w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                {c.sponsored_until && new Date(c.sponsored_until).getTime() > Date.now() && (
                  <span className="absolute top-2 left-2 rtl:left-auto rtl:right-2 bg-zinc-950 text-white text-[10px] font-mono uppercase tracking-wider px-2 py-1" data-testid={`conf-sponsored-tag-${idx}`}>✦ {t("conf.sponsored")}</span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] text-zinc-400 tracking-[0.15em]">№ {String(idx + 1).padStart(3, "0")}</span>
                  <span className="tag">{c.type === "tech" ? t("common.industry") : t("common.academic")}</span>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight leading-tight group-hover:text-blue-700 transition-colors">{c.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{c.description}</p>
                <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-600 font-mono">
                  <span className="flex items-center gap-1.5"><Calendar size={12} /> {format(new Date(c.start_date), "MMM d, yyyy")}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={12} /> {c.city}, {c.country}</span>
                </div>
                {c.cfp_deadline && (
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-blue-700">
                    {t("conf.callForPapers")} · {format(new Date(c.cfp_deadline), "MMM d, yyyy")}
                  </div>
                )}
              </div>
            </Link>
          </motion.div>
        ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2" data-testid="conf-pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="btn-secondary disabled:opacity-40">{t("common.prev")}</button>
          {Array.from({ length: Math.min(7, pages) }, (_, i) => {
            let start = Math.max(1, page - 3);
            let end = Math.min(pages, start + 6);
            start = Math.max(1, end - 6);
            const p = start + i;
            if (p > end) return null;
            return (
              <button key={p} onClick={() => setPage(p)} className={(p === page ? "btn-primary" : "btn-secondary") + " min-w-[44px] justify-center"} data-testid={`conf-page-${p}`}>{p}</button>
            );
          })}
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="btn-secondary disabled:opacity-40">{t("common.next")}</button>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid, labels, allLabel }) {
  return (
    <div className="md:col-span-2 lg:col-span-2">
      <label className="overline block mb-2">{label}</label>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none border-b border-zinc-300 bg-transparent py-2 pr-6 text-sm outline-none cursor-pointer focus:border-zinc-950"
        >
          <option value="">{allLabel || "All"}</option>
          {options.map((o) => (<option key={o} value={o}>{labels?.[o] || (o[0]?.toUpperCase() + o.slice(1))}</option>))}
        </select>
        <FunnelSimple size={12} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400 rtl:right-auto rtl:left-0" />
      </div>
    </div>
  );
}
